import Checkbox from '@material-ui/core/Checkbox';
import Link from 'components/Link';
import React from 'react';
import moment from 'moment';
import { NA } from '../../../content';
import { RateLevel, Routes } from '../../../constants';
import { Segment } from '../types';
import { buildSegmentDetailQueryParams } from './urlHelpers';
import { isNil } from 'lodash';
import * as Styled from '../index.styles';

export type SegmentRowSelection = {
  checked: boolean;
  disabled: boolean;
  onToggle: () => void;
};

/**
 * TTD platform row cells renderer
 * @param segment - The segment data object
 * @param baseCell - The base segment name cell (common across all platforms)
 * @returns React fragment containing table cells for the row
 */
const TTD_ROW_CELLS = (
  segment: Segment,
  baseCell: React.ReactElement
): React.ReactElement => {
  return (
    <>
      {baseCell}
      <td key="receivedIds">
        <Styled.NumberCell>
          {!isNil(segment?.receivedIds)
            ? segment.receivedIds.toLocaleString('en-US')
            : NA}
        </Styled.NumberCell>
      </td>
      <td key="activeIds">
        <Styled.NumberCell>
          {!isNil(segment?.activeIds)
            ? segment.activeIds.toLocaleString('en-US')
            : NA}
        </Styled.NumberCell>
      </td>
      <td key="cpm">
        <Styled.CurrencyCell>
          {!isNil(segment?.cpm) ? `$${segment.cpm.toFixed(2)}` : NA}
        </Styled.CurrencyCell>
      </td>
      <td key="pom">
        <Styled.NumberCell>
          {!isNil(segment?.pom)
            ? `${segment.pom.toLocaleString('en-US')}%`
            : NA}
        </Styled.NumberCell>
      </td>
      <td key="impressions">
        <Styled.NumberCell>
          {!isNil(segment?.impressions)
            ? segment.impressions.toLocaleString('en-US')
            : NA}
        </Styled.NumberCell>
      </td>
      <td key="effectiveCpm">
        <Styled.CurrencyCell>
          {!isNil(segment?.effectiveCpm)
            ? `$${segment.effectiveCpm.toFixed(2)}`
            : NA}
        </Styled.CurrencyCell>
      </td>
      <td key="revenue">
        <Styled.CurrencyCell>
          {!isNil(segment?.revenue)
            ? `$${Math.round(segment.revenue).toLocaleString('en-US')}`
            : NA}
        </Styled.CurrencyCell>
      </td>
      <td key="lastUpdatedAt">
        <Styled.LastUpdated>
          {!isNil(segment.lastUpdatedAt)
            ? moment(segment.lastUpdatedAt).format('MM/DD/YY ha')
            : NA}
        </Styled.LastUpdated>
      </td>
    </>
  );
};

/**
 * Utility function to render DataTable row cells based on platform type
 * @param platform - The platform type (e.g., 'TTD', 'META', etc.)
 * @param segment - The segment data object
 * @param selection - Row checkbox state
 * @returns React fragment containing table cells for the row
 */
export const renderRowCellsByPlatform = (
  platform: string,
  segment: Segment,
  selection: SegmentRowSelection
): React.ReactElement => {
  // Base cell: Segment Name (common across all platforms) - make it a link
  const platformLower = platform.toLowerCase();
  const segmentId = segment.id;

  // Build query params using helper
  const queryString = buildSegmentDetailQueryParams(
    segment.rateLevel || RateLevel.SYSTEM,
    segment.advertiserId,
    segment.partnerId
  );

  const linkUrl = `${Routes.SEGMENT_MANAGER}/${platformLower}/${segmentId}${
    queryString ? `?${queryString}` : ''
  }`;

  const selectCell = (
    <td
      key="select"
      style={{ width: 52, textAlign: 'center', verticalAlign: 'middle' }}
    >
      <Checkbox
        color="primary"
        checked={selection.checked}
        disabled={selection.disabled}
        onChange={() => selection.onToggle()}
        inputProps={{
          'aria-label': `Select ${segment.segmentName || segmentId}`,
        }}
      />
    </td>
  );

  const baseCell = (
    <td key="segmentName">
      <Styled.SegmentName title={segment.segmentName || ''}>
        <Link to={linkUrl}>{segment.segmentName}</Link>
        {/* Icon placeholder for errors or additional information */}
        {/* You can add conditional rendering here based on segment data */}
      </Styled.SegmentName>
    </td>
  );

  // Platform-specific cells
  switch (platform) {
    case 'TTD':
      return (
        <>
          {selectCell}
          {TTD_ROW_CELLS(segment, baseCell)}
        </>
      );

    // Add more platform cases here as needed
    // Example for other platforms:
    // case 'META':
    //   return (
    //     <>
    //       {baseCell}
    //       {/* Meta-specific cells */}
    //     </>
    //   );

    default:
      // Default to TTD cells if platform is not recognized
      return (
        <>
          {selectCell}
          {TTD_ROW_CELLS(segment, baseCell)}
        </>
      );
  }
};
